/**
 * Helpers compartidos por las 4 screens admin.
 *
 * - AdminGate: HOC que verifica is_admin antes de renderizar contenido.
 * - AdminBadge: chip "ADMIN" usado en headers para distinguir el área Nilabs.
 * - format helpers (COP, fecha relativa, fecha ISO local).
 * - colorFor: hash determinístico nombre → color paleta (para avatares).
 */
import type { ReactNode } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { EmptyState } from "../../lib/components";

export function AdminGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.is_admin) {
    return (
      <div className="page" style={{ paddingTop: 48 }}>
        <EmptyState
          icon="settings"
          title="Acceso denegado"
          body="Esta sección es solo para administradores de Nilabs. Si crees que debías tener acceso, escríbele a nilton.alfonso@nilabs.co."
        />
      </div>
    );
  }
  return <>{children}</>;
}

export function AdminBadge() {
  return <span className="al-admin-badge" title="Centro de control Nilabs">ADMIN</span>;
}

const COP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

export function formatCOP(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return COP.format(value);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-CO", { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return "—";
  }
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("es-CO", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export function relativeTime(iso?: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const min = Math.round(diff / 60000);
  if (min < 1) return "hace segundos";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 7) return `hace ${d} d`;
  const w = Math.round(d / 7);
  if (w < 5) return `hace ${w} sem`;
  const months = Math.round(d / 30);
  if (months < 12) return `hace ${months} m`;
  return `hace ${Math.round(d / 365)} a`;
}

export function initialsOf(name: string | null | undefined, email?: string | null): string {
  const n = (name || "").trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "·";
}

const PALETTE = ["#7c3aed", "#ec4899", "#059669", "#2563eb", "#f59e0b", "#dc2626", "#0891b2"];
export function colorFor(seed: string): string {
  if (!seed) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % PALETTE.length;
  return PALETTE[h];
}

export const TIER_LABELS: Record<string, string> = {
  free: "Free",
  solo: "Solo",
  pro: "Pro",
  studio: "Studio",
};

export function tierLabel(tier: string | null | undefined): string {
  if (!tier) return "—";
  return TIER_LABELS[tier] || tier;
}

/** Skeleton bloque rectangular para estados de carga. */
export function Skeleton({ height = 16, width = "100%" }: { height?: number; width?: number | string }) {
  return (
    <div
      className="al-admin-skel"
      style={{ height, width: typeof width === "number" ? `${width}px` : width }}
    />
  );
}
